import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface PartItem {
  id: string;
  name: string;
  description: string;
  price: number;
  qty: number;
}

export interface CreateOrderRequest {
  assetId: string;
  homeId: string;
  productName: string;  // Required by V1 backend
  parts: PartItem[];
  shippingAddress: string;
  diagnosisData: {
    issue: string;
    rootCauses: string[];
    diyResults: Record<number, "working" | "not-working">;
  };
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  totalAmount: number;
}

export interface Order {
  id: string;
  orderId: string;
  userId: string;
  orderType: string;
  status: string;
  productName: string;
  productBrand?: string;
  productModel?: string;
  productImage?: string;
  partCategory?: string;
  compatibleWith?: string;
  assetId?: string;
  totalAmount: number;
  subtotal: number;
  taxAmount?: number;
  shippingCost?: number;
  hasTradeIn?: boolean;
  oldAssetName?: string;
  trackingNumber?: string;
  carrier?: string;
  estimatedDelivery?: string;
  diagnosisIssue?: string;
  diagnosisData?: Record<string, unknown>;
  relatedBookingId?: string;
  orderDate?: string;
  createdAt: string;
  updatedAt: string;
  asset?: {
    id: string;
    name: string;
    category: string;
    location?: string | null;
    manufacturer?: string | null;
    model?: string | null;
    imageUrl?: string | null;
  };
}

// ── API Functions ────────────────────────────────────────────────────────────

/**
 * Create a new parts order
 */
export const createOrder = async (data: CreateOrderRequest): Promise<Order> => {
  return await api.post<Order>('/v1/orders', data);
};

/**
 * Get all orders for the current user
 */
export const getUserOrders = async (): Promise<Order[]> => {
  return await api.get<Order[]>('/v1/orders');
};

/**
 * Get a specific order by ID
 */
export const getOrderById = async (orderId: string): Promise<Order> => {
  return await api.get<Order>(`/v1/orders/${orderId}`);
};

/**
 * Cancel an order
 */
export const cancelOrder = async (orderId: string): Promise<Order> => {
  return await api.put<Order>(`/v1/orders/${orderId}/cancel`);
};

/**
 * Permanently delete an order
 */
export const deleteOrder = async (orderId: string): Promise<{ id: string }> => {
  return await api.delete<{ id: string }>(`/v1/orders/${orderId}`);
};
